import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import Link from 'next/link';
import { getProductAgeRating } from '~/utilities/ageCheck';
import { getAvailability } from '~/utilities/availability';

const UNAVAILABLE_COLOR = '#C4504A';

const AVAILABLE_COLOR = 'rgb(118, 186, 132)';

const PackageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2L12 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M4 7.2l8 4.3 8-4.3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M12 11.5V21" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M8 5.1l8 4.3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const BlockedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9.25" stroke={UNAVAILABLE_COLOR} strokeWidth="1.6" />
    <path d="M12 7.5v5.5" stroke={UNAVAILABLE_COLOR} strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="16.4" r="1" fill={UNAVAILABLE_COLOR} />
  </svg>
);

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" stroke="#000000" strokeWidth="1.6" />
    <path d="M3.5 10h17" stroke="#000000" strokeWidth="1.6" />
    <path d="M8 3v4M16 3v4" stroke="#000000" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="8.500" cy="14" r="1" fill="#000000" />
    <circle cx="12" cy="14" r="1" fill="#000000" />
    <circle cx="15.500" cy="14" r="1" fill="#000000" />
    <circle cx="8.500" cy="17.200" r="1" fill="#000000" />
    <circle cx="12" cy="17.200" r="1" fill="#000000" />
  </svg>
);

const PreorderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M9 15l2.2 2.2L15.5 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ModuleProductDetailSpecification = ({ product, selectedVariant }) => {
  const variantAttrs = selectedVariant?.attributes || {};
  const productAttrs = product?.attributes || {};
  const attrs = selectedVariant ? variantAttrs : productAttrs;

  const sku = attrs.sku || productAttrs.sku || 'N/A';
  const { product_categories, shippings } = productAttrs;
  const productName = attrs.title || productAttrs.title || 'Unbekanntes Produkt';

  const activeProduct = selectedVariant || product;
  const activeProductAttrs = activeProduct?.attributes || activeProduct || {};

  const shippingAttrs =
    activeProductAttrs.shippings?.data?.attributes ||
    shippings?.data?.attributes ||
    null;

  const availability = useMemo(
    () => getAvailability(activeProductAttrs, shippingAttrs),
    [activeProductAttrs, shippingAttrs]
  );

  const { status, deliveryText, dateLabel } = availability;

  const tooltipTexts = {
    available:
      'Der Artikel wird innerhalb der angegebenen Zeit von uns oder unseren RAWENTO Partnern für den Versand vorbereitet und Ihnen zugesendet. Den Status Ihrer Bestellung können Sie jederzeit über den Link zur Auftragsverfolgung einsehen, den wir Ihnen mit der Versandbestätigung per E-Mail senden.',
    preorder:
      'Dieser Artikel kann bereits vorbestellt werden. Sobald er verfügbar ist, bereiten wir ihn für den Versand vor und senden ihn Ihnen zu. Den Status Ihrer Bestellung können Sie jederzeit über den Link zur Auftragsverfolgung einsehen, den wir Ihnen mit der Versandbestätigung per E-Mail senden.',
    coming_soon: dateLabel
      ? `Dieser Artikel ist derzeit noch nicht bestellbar. Voraussichtlich ab dem ${dateLabel} können Sie ihn hier bestellen.`
      : 'Dieser Artikel ist derzeit noch nicht bestellbar. Sobald er verfügbar ist, können Sie ihn hier bestellen.',
    unavailable:
      'Dieser Artikel ist derzeit nicht auf Lager. Sobald der Artikel wieder verfügbar ist, können Sie ihn hier bestellen.',
  };
  const tooltipText = tooltipTexts[status];

  const ageRating = useMemo(
    () =>
      getProductAgeRating({
        minAge: attrs.minAge,
        ageRatingType: attrs.age_rating_type,
      }),
    [attrs.minAge, attrs.age_rating_type]
  );

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipShift, setTooltipShift] = useState(0);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const productCategories = useMemo(() => {
    const categories = product_categories?.data || [];
    if (!categories.length) return <p>Keine Kategorie gefunden.</p>;
    return categories.map((category) => (
      <Link href={`/category/${category.attributes?.slug}`} key={category.id}>
        {category.attributes?.title || 'Ohne Titel'}
      </Link>
    ));
  }, [product_categories]);

  const handleMouseEnter = useCallback(() => setTooltipVisible(true), []);
  const handleMouseLeave = useCallback((e) => {
    const relatedTarget = e.relatedTarget;
    const toTooltip = relatedTarget && tooltipRef.current?.contains(relatedTarget);
    const toTrigger = relatedTarget && triggerRef.current?.contains(relatedTarget);
    if (toTooltip || toTrigger) return;
    setTooltipVisible(false);
  }, []);

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setTooltipVisible(!tooltipVisible);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setTooltipVisible(false);
    };
    const handleClickOutside = (e) => {
      if (
        tooltipVisible &&
        !triggerRef.current?.contains(e.target) &&
        !tooltipRef.current?.contains(e.target)
      ) {
        setTooltipVisible(false);
      }
    };

    if (tooltipVisible) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tooltipVisible]);

  useLayoutEffect(() => {
    if (!tooltipVisible) return undefined;

    const adjustPosition = () => {
      const el = tooltipRef.current;
      const trigger = triggerRef.current;
      if (!el || !trigger) return;

      const padding = 12;
      const viewportWidth = window.innerWidth;
      const width = el.offsetWidth;
      const triggerRect = trigger.getBoundingClientRect();
      const anchorCenter = triggerRect.left + triggerRect.width / 2;

      const boxLeft = anchorCenter - width / 2;
      const boxRight = boxLeft + width;

      let shift = 0;
      if (boxLeft < padding) {
        shift = padding - boxLeft;
      } else if (boxRight > viewportWidth - padding) {
        shift = viewportWidth - padding - boxRight;
      }

      setTooltipShift(shift);
    };

    adjustPosition();
    window.addEventListener('resize', adjustPosition);
    return () => window.removeEventListener('resize', adjustPosition);
  }, [tooltipVisible]);

  const statusClass =
    status === 'unavailable'
      ? 'delivery-status--unavailable'
      : status === 'coming_soon'
        ? 'delivery-status--upcoming'
        : status === 'available'
          ? 'delivery-status--available'
          : '';

  return (
    <div className="ps-product__specification">
      <Link
        href={`mailto:info@rawento.com?subject=Frage zum Produkt ${sku} - ${productName}`}
        className="report"
        style={{ color: '#06c' }}
      >
        Frage zum Produkt
      </Link>

      <p>
        <strong>SKU:</strong> <span>{sku}</span>
      </p>

      <div className="ps-product__delivery-status">
        <div className="product-collect-and-delivery" style={{ position: 'relative' }}>
          <div className="product-collect-and-delivery-text">
            <div className={`delivery-status ${statusClass}`}>
              <span
                className="delivery-status-text"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                aria-describedby={tooltipVisible ? 'delivery-tooltip' : undefined}
              >
                {/* Icon vorne – ohne Tooltip */}
                <span
                  className="delivery-status-icon"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '3px',
                  }}
                >
                  {status === 'unavailable' ? (
                    <BlockedIcon />
                  ) : status === 'coming_soon' ? (
                    <CalendarIcon />
                  ) : status === 'preorder' ? (
                    <PreorderIcon />
                  ) : (
                    <PackageIcon />
                  )}
                </span>

                <strong>
                  {status === 'available' && (deliveryText ? 'Lieferbar:' : 'Lieferbar')}
                  {status !== 'available' && deliveryText}
                </strong>

                {/* (i) mit Tooltip hinter dem Text – für alle Status */}
                <span
                  className="delivery-time"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', position: 'relative' }}
                >
                  <span
                    ref={triggerRef}
                    id="delivery-tooltip-trigger"
                    className="tooltip-trigger"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onFocus={() => setTooltipVisible(true)}
                    onBlur={() => setTooltipVisible(false)}
                    onKeyDown={handleTriggerKeyDown}
                    tabIndex={0}
                    role="button"
                    aria-expanded={tooltipVisible}
                    aria-haspopup="true"
                    style={{ display: 'inline-flex', cursor: 'help', position: 'relative' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 32 32"
                      fill="#7A7575"
                      aria-hidden="true"
                    >
                      <g>
                        <g>
                          <path d="M16.196 17.808a.639.639 0 0 1-.639-.639v-.857a1.919 1.919 0 0 1 1.278-1.808 1.917 1.917 0 1 0-2.557-1.809.639.639 0 1 1-1.278 0 3.2 3.2 0 1 1 4.261 3.013.641.641 0 0 0-.426.6v.858a.639.639 0 0 1-.636.642Z" fill="#7A7575" />
                          <circle cx="16.196" cy="21.641" r="0.959" fill="#7A7575" />
                        </g>
                        <path d="M16 27.6A11.6 11.6 0 1 1 27.6 16 11.613 11.613 0 0 1 16 27.6Zm0-22A10.4 10.4 0 1 0 26.4 16 10.412 10.412 0 0 0 16 5.6Z" fill="#7A7575" />
                      </g>
                    </svg>
                    {tooltipVisible && (
                      <div
                        ref={tooltipRef}
                        id="delivery-tooltip"
                        className="tooltip-content"
                        role="tooltip"
                        aria-live="polite"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          transform: `translateX(calc(-50% + ${tooltipShift}px))`,
                          '--arrow-shift': `${tooltipShift}px`,
                        }}
                      >
                        {tooltipText}
                      </div>
                    )}
                  </span>
                  {status === 'available' ? deliveryText : null}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {ageRating && (
        <p className="ps-product__age-notice">
          Eine Lieferung an Minderjährige ist nicht möglich.{' '}
          <Link
            href="/company/age-verification"
            style={{ color: '#06c', textDecoration: 'underline' }}
          >
            Infos zu Titeln ohne Jugendfreigabe.
          </Link>
        </p>
      )}

      <style jsx>{`
        .report {
          color: #06c !important;
          text-decoration: none;
        }
        .report:hover,
        .report:focus {
          color: #06c !important;
          text-decoration: underline;
        }
        .tooltip-trigger {
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .tooltip-trigger:focus {
          outline: none;
        }
        .tooltip-content {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #333;
          color: #fff;
          padding: 8px 12px;
          border-radius: 4px;
          white-space: normal;
          max-width: 320px;
          width: 320px;
          word-wrap: break-word;
          line-height: 1.4;
          font-size: 12px;
          box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          margin-bottom: 8px;
          animation: fadeIn 0s ease-in;
        }
        .tooltip-content::after {
          content: '';
          position: absolute;
          top: 100%;
          left: calc(50% - var(--arrow-shift, 0px));
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #333;
          margin-top: -1px;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        .ps-product__delivery-status {
          margin-top: 5px;
          font-size: 14px;
          color: var(--color-text, #333);
        }
        .product-collect-and-delivery-text {
          font-size: 14px;
          color: var(--color-text, #333);
          position: relative;
        }
        .delivery-status {
          font-weight: bold;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .delivery-time {
          font-weight: normal;
          color: var(--color-text-muted, #555);
        }
        .delivery-status strong {
          font-weight: 700;
        }
        /* NEU: "Lieferbar" in Grün */
        .delivery-status--available strong {
          color: ${AVAILABLE_COLOR};
        }
        .delivery-status--unavailable strong {
          color: #C4504A;
        }
        .delivery-status--upcoming strong {
          color: #000000;
        }
        .delivery-time--unavailable {
          color: var(--color-text-muted, #555);
        }
        .ps-product__age-notice {
          margin-top: 8px;
          font-size: 12px;
          line-height: 1.4;
          color: var(--color-text-muted, #555);
        }

        @media (max-width: 420px) {
          .tooltip-content {
            width: calc(100vw - 24px);
            max-width: calc(100vw - 24px);
          }
        }
      `}</style>
    </div>
  );
};

export default ModuleProductDetailSpecification;